import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PasswordInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { api } from '../../lib/api';

export type CreateUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type SponsorPreview = {
  slotCode: string;
  ownerFullName: string | null;
  ownerEmail: string;
  membershipCode: string;
  slotType: 'paid' | 'free';
};

export function CreateUserDialog({ open, onOpenChange }: CreateUserDialogProps) {
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [sponsorCode, setSponsorCode] = useState('');
  const [status, setStatus] = useState<'active' | 'pending'>('active');
  const [error, setError] = useState<string | null>(null);
  // Two-step sponsor confirmation: when a sponsor code is entered, resolve and
  // show the sponsor and require an explicit Confirm before creating the user.
  const [sponsorPreview, setSponsorPreview] = useState<SponsorPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);

  function reset() {
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setPhone('');
    setSponsorCode('');
    setStatus('active');
    setError(null);
    setSponsorPreview(null);
    setPreviewing(false);
  }

  // Step 1 → if a sponsor code is present, resolve it and show the confirm panel;
  // otherwise create directly.
  async function handleCreate() {
    setError(null);
    if (sponsorCode.trim()) {
      setPreviewing(true);
      try {
        const res = await api.post<SponsorPreview>('/users/preview-sponsor', {
          sponsorCode: sponsorCode.trim(),
        });
        setSponsorPreview(res.data);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } } };
        const code = e.response?.data?.error ?? 'request_failed';
        setError(
          code === 'sponsor_not_found' ? 'Sponsor code not found.' : code.replace(/_/g, ' '),
        );
      } finally {
        setPreviewing(false);
      }
      return;
    }
    mut.mutate();
  }

  const mut = useMutation({
    mutationFn: async () =>
      (
        await api.post('/users', {
          email,
          password,
          firstName,
          middleName: middleName || undefined,
          lastName,
          phone: phone || undefined,
          sponsorCode: sponsorCode || undefined,
          status,
        })
      ).data,
    meta: { successMessage: 'User created.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      const code = e.response?.data?.error ?? 'request_failed';
      setError(code.replace(/_/g, ' '));
    },
  });

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= 8;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>
            Creates the member account. To activate a slot for them, use Create Slot on the Slots
            page after this completes.
          </DialogDescription>
        </DialogHeader>

        {sponsorPreview ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Confirm this is the correct sponsor before creating the account.
            </p>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="text-xs text-muted-foreground">New member will be sponsored by</div>
              <div className="font-medium">
                {sponsorPreview.ownerFullName ?? sponsorPreview.ownerEmail}
              </div>
              <div className="text-sm text-muted-foreground">
                Sponsor slot {sponsorPreview.slotCode || '—'}
                {sponsorPreview.membershipCode ? ` · ${sponsorPreview.membershipCode}` : ''}
              </div>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">First name</label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Middle name (optional)</label>
                <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Last name</label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Phone (optional)</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Password (min 8)</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Sponsor code (optional)</label>
                <Input value={sponsorCode} onChange={(e) => setSponsorCode(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={status} onValueChange={(v) => setStatus(v as 'active' | 'pending')}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="pending">pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          {sponsorPreview ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setSponsorPreview(null);
                  setError(null);
                }}
                disabled={mut.isPending}
              >
                Back
              </Button>
              <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
                {mut.isPending ? 'Creating…' : 'Confirm & create user'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mut.isPending || previewing}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!canSubmit || mut.isPending || previewing}>
                {previewing ? 'Checking sponsor…' : 'Create user'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
